import clsx from 'clsx'
import type { LocalVideoTrack, RemoteTrack } from 'livekit-client'
import { useEffect, useRef } from 'react'
import type { RtParticipantInfo } from '../rt/use-rt-room'
import { useAudioLevel } from '../lib/use-audio-level'
import { LevelMeterPro } from './LevelMeterPro'
import { StatusLamp } from './slp/StatusLamp'
import '../styles/slp.css'

/**
 * Zoom-style camera strip (rt UI v2 spec §画面传输 #1) — one tile per
 * participant with a published camera: video + name + role + mini level
 * meter (data-testid rt-vu-<identity>) + presence lamp.
 */

type AnyVideoTrack = LocalVideoTrack | RemoteTrack

function VideoSurface({ track, mirror }: { track: AnyVideoTrack | null; mirror?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !track) return
    const media = track.attach()
    media.style.width = '100%'
    media.style.height = '100%'
    media.style.objectFit = 'cover'
    if (mirror) media.style.transform = 'scaleX(-1)'
    el.appendChild(media)
    return () => {
      track.detach(media)
      media.remove()
    }
  }, [track, mirror])
  return <div ref={ref} className="absolute inset-0" />
}

const ROLE_COLOR: Record<string, string> = {
  studio: '#29ABE2',
  director: '#8B5CF6',
  engineer: '#06B6D4',
  actor: '#F97316',
  unknown: '#9aa6ad',
}

function Tile({
  p,
  track,
  stream,
  online,
  isLocal,
  cameraEnabled,
  onToggleCamera,
}: {
  p: RtParticipantInfo
  track: AnyVideoTrack | null
  stream: MediaStream | null
  online: boolean
  isLocal: boolean
  cameraEnabled?: boolean
  onToggleCamera?: (on: boolean) => void
}) {
  const db = useAudioLevel(stream, { intervalMs: 16 })
  return (
    <div
      data-testid={`rt-video-tile-${p.identity}`}
      data-role={p.role}
      className="relative aspect-video w-44 shrink-0 overflow-hidden rounded-md border border-[var(--slp-border)] bg-[#0b0b0c]"
    >
      <VideoSurface track={track} mirror={isLocal} />
      {!track ? (
        <div className="absolute inset-0 flex items-center justify-center text-[11px] text-white/40">
          camera off
        </div>
      ) : null}
      {isLocal && onToggleCamera ? (
        <button
          type="button"
          data-testid="rt-camera-toggle"
          onClick={() => onToggleCamera(!cameraEnabled)}
          className="absolute right-1 top-1 z-10 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white hover:bg-black/80"
        >
          {cameraEnabled ? 'Cam off' : 'Cam on'}
        </button>
      ) : null}
      <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col gap-0.5 bg-black/55 px-1.5 py-1">
        <div className="flex items-center gap-1.5">
          <StatusLamp status={online ? 'ok' : 'off'} size="sm" />
          <span className="truncate text-[11px] font-medium text-white">
            {p.name}
            {isLocal ? ' (you)' : ''}
          </span>
          <span
            className="ml-auto shrink-0 rounded px-1 text-[9px] font-bold uppercase leading-4 text-white"
            style={{ background: ROLE_COLOR[p.role] ?? ROLE_COLOR['unknown'] }}
          >
            {p.role}
          </span>
        </div>
        <div data-testid={`rt-vu-${p.identity}`}>
          <LevelMeterPro id={`vu-${p.identity}`} peakDbfs={db} variant="horizontal" className="rt-vu-mini" />
        </div>
      </div>
    </div>
  )
}

export function VideoGrid({
  participants,
  localCameraTrack,
  localMicStream,
  remoteCameras,
  remoteAudioStreams,
  cameraEnabled,
  onToggleCamera,
  className,
}: {
  participants: RtParticipantInfo[]
  localCameraTrack: LocalVideoTrack | null
  localMicStream: MediaStream | null
  remoteCameras: Record<string, RemoteTrack>
  remoteAudioStreams: Record<string, MediaStream>
  cameraEnabled: boolean
  onToggleCamera: (on: boolean) => void
  className?: string
}) {
  return (
    <div
      data-testid="rt-video-grid"
      className={clsx(
        'flex gap-2 overflow-x-auto rounded-md border border-[var(--slp-border)] bg-[var(--slp-panel-2)] p-2',
        className,
      )}
    >
      {participants.map((p) => {
        const stale = p.statusAtMs !== null && Date.now() - p.statusAtMs > 12_000
        return (
          <Tile
            key={p.identity}
            p={p}
            isLocal={p.isLocal}
            online={!stale}
            track={p.isLocal ? localCameraTrack : (remoteCameras[p.identity] ?? null)}
            stream={p.isLocal ? localMicStream : (remoteAudioStreams[p.identity] ?? null)}
            cameraEnabled={p.isLocal ? cameraEnabled : undefined}
            onToggleCamera={p.isLocal ? onToggleCamera : undefined}
          />
        )
      })}
      {participants.length === 0 ? (
        <div className="px-3 py-6 text-[12px] text-[var(--slp-text-dim)]">No participants yet</div>
      ) : null}
    </div>
  )
}
